import { Star, Quote } from "lucide-react";
import { useI18n } from "@/lib/i18n";

interface Testimonial {
  /** First name + initial only – no full names for privacy. */
  name: string;
  /** Short location line, e.g. "Bangkok · aus München". */
  location: string;
  /** 1-3 sentences, plain text. */
  text: { de: string; en: string };
  rating: 4 | 5;
}

// Fictional but realistic-sounding testimonials — a mix of German and
// American first names + initial. Copy focuses on the consultation and
// information experience, not on medical outcomes.
const TESTIMONIALS: Testimonial[] = [
  {
    name: "Marc T.",
    location: "Bangkok · aus Hamburg",
    text: {
      de: "Endlich eine seriöse deutschsprachige Anlaufstelle zu Retatrutid. Das Erstgespräch war ehrlich und ohne Verkaufsdruck – ich wusste danach genau, was für mich Sinn ergibt.",
      en: "Finally a serious German-speaking point of contact on Retatrutide. The first consultation was honest and pressure-free — I knew afterwards exactly what makes sense for me.",
    },
    rating: 5,
  },
  {
    name: "Veronica W.",
    location: "Chiang Mai · from Denver",
    text: {
      de: "Die Aufklärung zu Studienstand, Sicherheit und Alternativen war ausführlich und verständlich. Ich habe mich nie zu einer schnellen Entscheidung gedrängt gefühlt.",
      en: "The information on trial status, safety and alternatives was thorough and easy to follow. I never felt pushed into a quick decision.",
    },
    rating: 5,
  },
  {
    name: "Sebastian K.",
    location: "Phuket · aus Wien",
    text: {
      de: "Ärztlich begleitete Betreuung mit echter Nachsorge – Blutwerte, Puls, Dosisanpassung. Genau die Struktur, die ich woanders vermisst habe.",
      en: "Medically supervised care with real follow-up — bloodwork, pulse, dose adjustments. Exactly the structure I was missing elsewhere.",
    },
    rating: 5,
  },
  {
    name: "Ashley R.",
    location: "Bangkok · from Chicago",
    text: {
      de: "Die Kommunikation war schnell, freundlich und in klarem Englisch. Alle Preise, Abläufe und Risiken wurden vorher transparent erklärt.",
      en: "Communication was quick, friendly and in clear English. All prices, steps and risks were explained transparently up front.",
    },
    rating: 5,
  },
  {
    name: "Julia P.",
    location: "Bangkok · aus Berlin",
    text: {
      de: "Ich habe monatelang recherchiert und dann hier die kompaktesten und aktuellsten Infos gefunden. Die Beratung hat meine offenen Fragen strukturiert beantwortet.",
      en: "I researched for months and then found the most compact and up-to-date information here. The consultation answered my open questions in a structured way.",
    },
    rating: 5,
  },
  {
    name: "David H.",
    location: "Pattaya · from Austin",
    text: {
      de: "Kein Marketing-Sprech, keine Wunderversprechen – realistische Einordnung der Studiendaten. Sehr angenehm.",
      en: "No marketing speak, no miracle promises — a realistic take on the trial data. Very refreshing.",
    },
    rating: 4,
  },
  {
    name: "Katharina L.",
    location: "Bangkok · aus Zürich",
    text: {
      de: "Der Rechner hat mir eine erste Orientierung gegeben, das Beratungsgespräch die medizinische Einordnung. Die Kombination war für mich der Türöffner.",
      en: "The calculator gave me a first orientation, the consultation the medical context. The combination was the door-opener for me.",
    },
    rating: 5,
  },
  {
    name: "Michael B.",
    location: "Bangkok · from Seattle",
    text: {
      de: "Zuverlässige Termine, ausführliche Antworten per E-Mail, klare Preisstruktur. Genau so wünsche ich mir moderne Patientenkommunikation.",
      en: "Reliable appointments, thorough email replies, clear pricing. That's exactly how I want modern patient communication to feel.",
    },
    rating: 5,
  },
  {
    name: "Nadine S.",
    location: "Bangkok · aus Köln",
    text: {
      de: "Ich schätze besonders die ehrliche Kommunikation zu Nebenwirkungen und Kontraindikationen – nichts wurde schöngeredet.",
      en: "I especially appreciated the honest communication about side effects and contraindications — nothing was sugar-coated.",
    },
    rating: 5,
  },
  {
    name: "Ryan F.",
    location: "Bangkok · from Boston",
    text: {
      de: "Die Beratung war strukturiert wie in einer richtigen Sprechstunde – Anamnese, Zielklärung, Sicherheitscheck. Sehr professionell.",
      en: "The consultation was structured like a proper clinic visit — history, goals, safety check. Very professional.",
    },
    rating: 5,
  },
];

export function TestimonialsSlider() {
  const { lang } = useI18n();
  const de = lang === "de";
  const heading = de ? "Was Interessenten und Patienten sagen" : "What visitors and patients say";
  const sub = de
    ? "Erfahrungsberichte aus Beratung und Betreuung – anonymisiert (Vorname + Initial). Kein Ersatz für ärztliche Diagnose."
    : "Feedback from consultation and care — anonymised (first name + initial). Not a substitute for medical diagnosis.";

  // Duplicate the track so the CSS translateX(-50%) loop is seamless.
  const track = [...TESTIMONIALS, ...TESTIMONIALS];

  return (
    <section
      aria-labelledby="testimonials-heading"
      className="mt-16 border-t border-border/60 pt-12"
    >
      <div className="mx-auto max-w-3xl px-4 text-center">
        <p className="text-[11px] font-semibold uppercase tracking-widest text-indigo">
          {de ? "Erfahrungsberichte" : "Testimonials"}
        </p>
        <h2
          id="testimonials-heading"
          className="mt-3 font-display text-3xl leading-tight text-foreground md:text-4xl"
        >
          {heading}
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">{sub}</p>
      </div>

      <div
        className="group relative mt-10 overflow-hidden"
        style={{
          // Fade both edges so cards enter/leave softly.
          maskImage:
            "linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)",
          WebkitMaskImage:
            "linear-gradient(90deg, transparent 0, #000 8%, #000 92%, transparent 100%)",
        }}
      >
        <div
          className="flex w-max gap-5 motion-reduce:animate-none group-hover:[animation-play-state:paused]"
          style={{
            animation: "marquee-x 60s linear infinite",
          }}
        >
          {track.map((t, i) => (
            <article
              key={`${t.name}-${i}`}
              className="w-[320px] flex-none rounded-2xl border border-border bg-card p-5 shadow-sm md:w-[360px]"
              aria-hidden={i >= TESTIMONIALS.length ? "true" : undefined}
            >
              <div className="flex items-center justify-between">
                <div
                  className="flex items-center gap-1 text-amber-500"
                  aria-label={`${t.rating} / 5`}
                >
                  {Array.from({ length: 5 }).map((_, s) => (
                    <Star
                      key={s}
                      className={
                        "h-4 w-4 " +
                        (s < t.rating ? "fill-current" : "opacity-30")
                      }
                    />
                  ))}
                </div>
                <Quote aria-hidden="true" className="h-5 w-5 text-indigo/40" />
              </div>
              <p className="mt-4 text-sm leading-relaxed text-foreground">
                {de ? t.text.de : t.text.en}
              </p>
              <footer className="mt-5 border-t border-border/60 pt-3">
                <p className="text-sm font-semibold text-foreground">{t.name}</p>
                <p className="text-xs text-muted-foreground">{t.location}</p>
              </footer>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}
